// Estado e mutações das opções de venda de um item de PDV: a lista de telas, a
// tela escolhida, o componente em edição e as quatro operações que mexem nos
// componentes dela.
//
// Por que num hook: as quatro funções abaixo compartilham exatamente o mesmo
// trio de estados. Enquanto elas moravam no corpo do modal, esse trio ficava
// misturado com os campos do formulário e com o estado do arraste.
import { useCallback, useState } from "react";
import { COMPONENT_TYPES } from "./catalogoDeComponentes";
import { PDVComponent } from "./tipos";

export function useOpcoesDeVenda() {
  const [saleOptions, setSaleOptions] = useState<any[]>([]);
  const [selectedSaleOption, setSelectedSaleOption] = useState<any>(null);
  const [editingComponent, setEditingComponent] = useState<PDVComponent | null>(
    null
  );

  const addComponentToSaleOption = useCallback(
    (componentType: string) => {
      if (!selectedSaleOption) return;

      const componentTypeInfo = COMPONENT_TYPES.find(
        (c) => c.id === componentType
      );
      const existingComponents =
        selectedSaleOption.parsedValue.saleOptionComponents || [];

      const newComponent: PDVComponent = {
        componentId: `comp-${Date.now()}`,
        componentName: componentTypeInfo?.name || componentType,
        componentType: componentType,
        componentProps: {
          componentBackgroundColor: "#1D50FF",
          componentColor: "#FFFFFF",
          componentWidth: "120px",
          componentHeight: "48px",
          componentPosition: {
            x: `${20 + existingComponents.length * 10}%`,
            y: `${20 + existingComponents.length * 8}%`,
          },
        },
        componentStatus: "visible",
      };

      const updatedOptions = saleOptions.map((option) =>
        option.itemInfoUuid === selectedSaleOption.itemInfoUuid
          ? {
              ...option,
              parsedValue: {
                ...option.parsedValue,
                saleOptionComponents: [...existingComponents, newComponent],
              },
            }
          : option
      );

      setSaleOptions(updatedOptions);
      setSelectedSaleOption(
        updatedOptions.find(
          (opt) => opt.itemInfoUuid === selectedSaleOption.itemInfoUuid
        )
      );
      setEditingComponent(newComponent);
    },
    [selectedSaleOption, saleOptions]
  );

  const removeComponentFromSaleOption = (componentId: string) => {
    if (!selectedSaleOption) return;

    const updatedOptions = saleOptions.map((option) =>
      option.itemInfoUuid === selectedSaleOption.itemInfoUuid
        ? {
            ...option,
            parsedValue: {
              ...option.parsedValue,
              saleOptionComponents:
                option.parsedValue.saleOptionComponents?.filter(
                  (comp: any) => comp.componentId !== componentId
                ) || [],
            },
          }
        : option
    );

    setSaleOptions(updatedOptions);
    setSelectedSaleOption(
      updatedOptions.find(
        (opt) => opt.itemInfoUuid === selectedSaleOption.itemInfoUuid
      )
    );
    if (editingComponent?.componentId === componentId) {
      setEditingComponent(null);
    }
  };

  const updateComponentInSaleOption = (componentId: string, updates: any) => {
    if (!selectedSaleOption) return;

    const updatedOptions = saleOptions.map((option) =>
      option.itemInfoUuid === selectedSaleOption.itemInfoUuid
        ? {
            ...option,
            parsedValue: {
              ...option.parsedValue,
              saleOptionComponents: (
                option.parsedValue.saleOptionComponents || []
              ).map((comp: any) =>
                comp.componentId === componentId
                  ? { ...comp, ...updates }
                  : comp
              ),
            },
          }
        : option
    );

    setSaleOptions(updatedOptions);
    setSelectedSaleOption(
      updatedOptions.find(
        (opt) => opt.itemInfoUuid === selectedSaleOption.itemInfoUuid
      )
    );
    if (editingComponent?.componentId === componentId) {
      setEditingComponent({ ...editingComponent, ...updates });
    }
  };

  const handleComponentPropChange = (
    componentId: string,
    propPath: string,
    value: any
  ) => {
    if (!selectedSaleOption) return;

    const updatedOptions = saleOptions.map((option) =>
      option.itemInfoUuid === selectedSaleOption.itemInfoUuid
        ? {
            ...option,
            parsedValue: {
              ...option.parsedValue,
              saleOptionComponents: (
                option.parsedValue.saleOptionComponents || []
              ).map((comp: any) => {
                if (comp.componentId === componentId) {
                  const newProps = { ...comp.componentProps };
                  const pathParts = propPath.split(".");
                  let current = newProps;

                  for (let i = 0; i < pathParts.length - 1; i++) {
                    if (!current[pathParts[i]]) {
                      current[pathParts[i]] = {};
                    }
                    current = current[pathParts[i]];
                  }

                  current[pathParts[pathParts.length - 1]] = value;
                  return { ...comp, componentProps: newProps };
                }
                return comp;
              }),
            },
          }
        : option
    );

    setSaleOptions(updatedOptions);
    setSelectedSaleOption(
      updatedOptions.find(
        (opt) => opt.itemInfoUuid === selectedSaleOption.itemInfoUuid
      )
    );
  };

  return {
    saleOptions,
    setSaleOptions,
    selectedSaleOption,
    setSelectedSaleOption,
    editingComponent,
    setEditingComponent,
    addComponentToSaleOption,
    removeComponentFromSaleOption,
    updateComponentInSaleOption,
    handleComponentPropChange,
  };
}
